import React from 'react';
import Message from './Message';
import { Message as MessageType } from '../types';

interface ChatContainerProps {
  messages: MessageType[];
  showWelcome: boolean;
  isTyping: boolean;
  isSearching: boolean;
  searchStatus: string;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  chatMessagesRef?: React.RefObject<HTMLDivElement>;
  appMode?: 'pawco' | 'genius-dog';
  onSuggestionClick?: (text: string) => void;
}

const PAWCO_SUGGESTIONS = [
  { icon: 'fa-leaf', text: 'What makes Pawco unique?' },
  { icon: 'fa-seedling', text: 'Why is plant-based better for my dog?' },
  { icon: 'fa-star', text: 'Tell me about the benefits of LuxBites' },
  { icon: 'fa-bowl-food', text: 'What are GreenBites flavors?' },
  { icon: 'fa-shield-heart', text: 'How do Pawco products support gut health?' },
  { icon: 'fa-calculator', text: 'How much should I feed my dog daily?' },
];

const GENIUS_DOG_SUGGESTIONS = [
  { icon: 'fa-graduation-cap', text: 'How does the Genius Dog Academy work?' },
  { icon: 'fa-box-open', text: 'What comes in each subscription box?' },
  { icon: 'fa-brain', text: 'How do GeniusTeas support my dog\'s health?' },
  { icon: 'fa-trophy', text: 'What\'s the difference between subscription tiers?' },
];

const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  showWelcome,
  isTyping,
  isSearching,
  searchStatus,
  messagesEndRef,
  chatMessagesRef,
  appMode = 'pawco',
  onSuggestionClick
}) => {
  const suggestions = appMode === 'genius-dog' ? GENIUS_DOG_SUGGESTIONS : PAWCO_SUGGESTIONS;

  return (
    <div className="chat-container">
      <div className="chat-messages" ref={chatMessagesRef}>
        {showWelcome && (
          <div className="welcome-section">
            <div className={`ai-avatar ${appMode === 'genius-dog' ? 'genius-dog-avatar' : ''}`}>
              {appMode === 'genius-dog' ? (
                <img 
                  src="/LightModeGeniusDog.png" 
                  alt="Genius Dog Logo" 
                  className="genius-dog-welcome-logo-large"
                />
              ) : (
                <>
                  <img 
                    src="/NewPawcoLogoMinimal.png" 
                  alt="Pawco Logo"
                  className="pawco-welcome-logo-large"
                />
                  <span className="ai-label">Pawco AI</span>
                </>
              )}
            </div>
            <div className="welcome-content">
              <div className="welcome-badge">
                <i className={`fas ${appMode === 'genius-dog' ? 'fa-brain' : 'fa-shield-alt'}`}></i>
                <span>{appMode === 'genius-dog' ? 'Welcome to Genius Dog!' : 'Welcome to Pawco!'}</span>
              </div>
              <h2 className="welcome-title">
                {appMode === 'genius-dog' 
                  ? 'Premium Dog Food Line Reimagined as the Genius Dog Academy'
                  : 'Built on Veterinary Research. Designed for Your Dog.'
                }
              </h2>
              <p className="welcome-subtitle">
                {appMode === 'genius-dog'
                  ? 'Ask anything about Genius Dog products, subscriptions, and the academy journey.'
                  : 'Ask me anything about Pawco products, dog nutrition, feeding guides, and more.'
                }
              </p>
              <div className="prompt-suggestions-grid">
                {suggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    className="prompt-suggestion-card"
                    onClick={() => onSuggestionClick?.(suggestion.text)}
                  >
                    <div className="suggestion-icon-wrap">
                      <i className={`fas ${suggestion.icon}`}></i>
                    </div>
                    <div className="suggestion-text-wrap">
                      <span className="suggestion-label">{suggestion.text}</span>
                      <span className="suggestion-cta">Ask now →</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

              {messages.map((message, index) => {
                const isLastMessage = index === messages.length - 1;
                const isStreamingMessage = isLastMessage && (isTyping || isSearching) && message.sender === 'bot';
                
                // Find the previous user message for bot responses
                let userQuestion = '';
                if (message.sender === 'bot') {
                  for (let i = index - 1; i >= 0; i--) {
                    if (messages[i].sender === 'user') {
                      userQuestion = messages[i].text;
                      break;
                    }
                  }
                }
                
                return (
                  <Message
                    key={message.id}
                    message={message}
                    isStreaming={isStreamingMessage}
                    webSearchResults={message.webSearchResults}
                    researchPapers={message.researchPapers}
                    userQuestion={userQuestion}
                    appMode={appMode}
                  />
                );
              })}

        {isSearching && (
          <div className="professional-search-indicator">
            <div className="search-animation">
              <div className="search-icon">
                <i className="fas fa-search"></i>
              </div>
              <div className="search-progress">
                <div className="progress-bar">
                  <div className="progress-fill"></div>
                </div>
              </div>
            </div>
            <div className="search-status">
              <span className="status-text">{searchStatus || 'Searching veterinary databases...'}</span>
              <div className="search-sources">
                <span className="source-tag">AVMA</span>
                <span className="source-tag">ASPCA</span>
                <span className="source-tag">FDA</span>
              </div>
            </div>
          </div>
        )}

        {isTyping && !isSearching && (
          <div className="professional-typing-indicator">
            <div className="typing-avatar">
              <img src="/NewPawcoLogoMinimal.png" alt="Pawco" className="avatar-logo" />
            </div>
            <div className="typing-content">
              <div className="typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="typing-text">Pawco is crafting your response...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatContainer;
